import { useQuery } from "@tanstack/react-query";
import { formatBRL } from "@yatta/domain";
import { env } from "../lib/env";
import { supabase } from "../lib/supabase";
import { useTenantRuntime } from "../config/TenantRuntime";

interface FlowAgreementRow {
  id: string;
  number: string;
  agreement_kind: string;
  source_kind: string;
  status: string;
  updated_at: string;
}

interface FlowReceivableRow {
  id: string;
  billing_agreement_id: string;
  billing_agreements: { number: string } | { number: string }[] | null;
  sequence: number;
  description: string;
  due_date: string;
  original_amount_cents: number;
  paid_amount_cents: number;
  status: string;
}

async function loadOrganizationId(slug: string): Promise<string> {
  const { data, error } = await supabase
    .from("organizations")
    .select("id")
    .eq("slug", slug)
    .single();
  if (error) throw error;
  const organization = data as { id: string } | null;
  if (!organization) throw new Error("Parceiro não encontrado para este ambiente.");
  return organization.id;
}

export function useFlowAgreements(enabled = true) {
  const { tenant } = useTenantRuntime();
  return useQuery({
    queryKey: ["flow-agreements", tenant.slug],
    enabled,
    queryFn: async () => {
      if (env.useMocks) return [];
      const organizationId = await loadOrganizationId(tenant.slug);
      const { data, error } = await supabase
        .from("billing_agreements")
        .select("id,number,agreement_kind,source_kind,status,starts_at,updated_at")
        .eq("organization_id", organizationId)
        .order("updated_at", { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as FlowAgreementRow[];
      return rows.map((row) => ({
        id: row.id,
        number: row.number,
        type: row.agreement_kind,
        origin: row.source_kind,
        status: row.status,
        updatedAt: new Date(row.updated_at).toLocaleDateString("pt-BR"),
      }));
    },
  });
}

export function useFlowReceivables(enabled = true) {
  const { tenant } = useTenantRuntime();
  return useQuery({
    queryKey: ["flow-receivables", tenant.slug],
    enabled,
    queryFn: async () => {
      if (env.useMocks) return [];
      const organizationId = await loadOrganizationId(tenant.slug);
      const { data, error } = await supabase
        .from("receivable_obligations")
        .select(
          "id,billing_agreement_id,sequence,description,due_date,original_amount_cents,paid_amount_cents,status,billing_agreements(number)",
        )
        .eq("organization_id", organizationId)
        .order("due_date", { ascending: true });
      if (error) throw error;
      const rows = (data ?? []) as FlowReceivableRow[];
      return rows.map((row) => ({
        id: row.id,
        agreement:
          (Array.isArray(row.billing_agreements)
            ? row.billing_agreements[0]?.number
            : row.billing_agreements?.number) ?? row.billing_agreement_id.slice(0, 8),
        description: row.description,
        sequence: String(row.sequence),
        dueDate: new Date(`${row.due_date}T12:00:00`).toLocaleDateString("pt-BR"),
        original: formatBRL(BigInt(row.original_amount_cents)),
        paid: formatBRL(BigInt(row.paid_amount_cents)),
        status: row.status,
      }));
    },
  });
}
